import type { FieldDefinition, Filter, SavedView, Sort } from "@estelle/domain";
import { Ellipsis } from "lucide-react";
import type { ReactNode } from "react";

import { ColumnChooser } from "@/components/columns/ColumnChooser";
import type { ColumnState } from "@/components/columns/columns";
import {
  CommonFilters,
  MobileFiltersButton,
} from "@/components/filters/CommonFilters";
import { FilterInput } from "@/components/filters/FilterInput";
import type { ListLayout } from "@/components/list/layout";
import { LayoutSwitcher } from "@/components/list/LayoutSwitcher";
import { SortControl } from "@/components/table/SortControl";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { SaveViewButton } from "@/components/views/SaveViewButton";
import { SavedViewPicker } from "@/components/views/SavedViewPicker";
import { useSavedViews } from "@/components/views/useSavedViews";
import { ViewTabs } from "@/components/views/ViewTabs";
import { useIsMobile } from "@/hooks/use-mobile";

/**
 * The list toolbar, as one bar (#349).
 *
 * Where the toolbar it replaces gave search, the saved-view tabs, Filter,
 * Sort, Columns and the applied chips a row each — two of them, once the
 * chips wrapped — this is the one row the reference screenshots showed: view
 * picker, the search-and-chips field, then Columns, Sort, the layout switch
 * where a section offers more than a table, and Save. Nothing here wraps to
 * a second line; on a narrow screen the field's own chips scroll sideways
 * instead (`FilterInput`), which is what keeps a phone down to the one row
 * too.
 *
 * Below `md` the row still has four icon buttons trying to share space with
 * search, which is what search actually needs (#357). View picker, search
 * and the filter button stay; Columns, Sort and Save move into one "⋯"
 * overflow — chosen over dropping any of them, since Save losing its own
 * button would bury "the list has changed since this view" a tap deeper
 * exactly when someone is most likely to want it.
 *
 * Still built entirely from F3's pieces — `FilterInput` is the only thing
 * new to this issue, and it in turn is built on `tree.ts`, `describe.ts` and
 * `ConditionRow` rather than a second filter model. Everything else here is
 * the same `ColumnChooser`, `SortControl`, `LayoutSwitcher`, `SavedViewPicker`
 * and `SaveViewButton` a screen would have reached for before, wired to the
 * same `apply()` a section's route already owns.
 */
export function FilterBar({
  scope,
  emptyLabel,
  placeholder,
  fields,
  filter,
  search,
  sort,
  columns,
  layout,
  layouts,
  listControls = true,
  onChangeFilter,
  onChangeSearch,
  onClearAll,
  onChangeSort,
  onChangeColumns,
  onChangeLayout,
  onSelectView,
  common = [],
  countOf,
  showLabel,
  moreActions,
  toolsSlot,
}: {
  /** Rare page actions the phone keeps in "⋯" — Export (#643). */
  moreActions?: ReactNode;
  /** Receives the phone's tools slot, where the list's Select goes (#647). */
  toolsSlot?: (element: HTMLElement | null) => void;
  /** The list's common filters, as chips under the search box (#633). */
  common?: FieldDefinition[];
  /** How many rows a filter holds, for the phone's view tabs (#633). */
  countOf?: (filter: Filter) => Promise<number | null>;
  /** The phone sheet's closing button: "Show 78 orders". */
  showLabel?: string;
  scope: string;
  emptyLabel: string;
  /** Ghost text once the input holds no chips — "Search orders". */
  placeholder: string;
  fields: FieldDefinition[];
  filter: Filter;
  search: string | undefined;
  sort: Sort[];
  columns: ColumnState;
  layout: ListLayout;
  layouts: readonly ListLayout[];
  /**
   * False for a section that is a chart rather than a list: the column chooser
   * and the sort control have no rows to act on there, so they are not offered.
   */
  listControls?: boolean;
  onChangeFilter: (next: Filter) => void;
  onChangeSearch: (next: string | undefined) => void;
  /** The X in the search field: filter and search cleared in one navigation. */
  onClearAll?: () => void;
  onChangeSort: (next: Sort[]) => void;
  onChangeColumns: (next: ColumnState) => void;
  onChangeLayout: (next: ListLayout) => void;
  /**
   * Applying a view touches all three at once, and the section's `apply()`
   * already knows how to write them as one search update rather than three —
   * calling `onChangeFilter`, then `onChangeColumns`, then `onChangeSort`
   * here instead would have each call close over the *previous* render's
   * values for the other two, so the second and third calls would each
   * overwrite what the one before it just set. One combined callback is what
   * `SavedViewTabs` did before it, for the same reason.
   */
  onSelectView: (view: SavedView | null) => void;
}) {
  const current = { filter, columns, sort };
  /*
    One instance, shared by the picker and Save, rather than each calling
    `useSavedViews(scope)` for itself (#355). Each call keeps its own
    `activeViewId` — recording "the open view" in one and reading it from
    the other is impossible when they are two different pieces of state.
  */
  const savedViews = useSavedViews(scope);
  const isMobile = useIsMobile();

  // Cards have no columns to choose, so the control that would do nothing
  // is not offered, on either layout.
  const columnChooser =
    listControls && layout === "table" ? (
      <ColumnChooser
        value={columns}
        fields={fields}
        compact={!isMobile}
        onChange={onChangeColumns}
      />
    ) : null;

  const sortControl = listControls ? (
    <SortControl
      fields={fields}
      sort={sort}
      compact={!isMobile}
      onChange={onChangeSort}
    />
  ) : null;

  const saveButton = (
    <SaveViewButton savedViews={savedViews} current={current} />
  );

  const layoutSwitcher =
    layouts.length > 1 ? (
      <LayoutSwitcher
        value={layout}
        offered={layouts}
        onChange={onChangeLayout}
      />
    ) : null;

  const viewPicker = (
    <SavedViewPicker
      scope={scope}
      savedViews={savedViews}
      emptyLabel={emptyLabel}
      current={current}
      onSelect={onSelectView}
    />
  );

  const filterInput = (
    <FilterInput
      value={filter}
      fields={fields}
      q={search}
      onChangeFilter={onChangeFilter}
      onChangeSearch={onChangeSearch}
      onClearAll={onClearAll}
      placeholder={placeholder}
      common={common}
      showLabel={showLabel}
    />
  );

  // "+ Filter" and the common chips, under the search box (#633).
  const commonFilters = (
    <CommonFilters
      fields={fields}
      common={common}
      value={filter}
      onChangeFilter={onChangeFilter}
      showLabel={showLabel}
    />
  );

  /*
    Two rows on a phone (#647): search with Filters and "⋯" beside it, then
    the saved views as tabs with their counts, ending in the list's Select.
    A third row of filter chips ran off the screen's edge and, with the tabs
    and a Select row, used the top 40% of the screen before any order.
  */
  if (isMobile) {
    return (
      <div className="flex min-w-0 flex-col gap-2">
        <div className="flex min-w-0 items-center gap-2">
          {filterInput}
          <MobileFiltersButton
            fields={fields}
            common={common}
            value={filter}
            onChangeFilter={onChangeFilter}
            showLabel={showLabel}
          />

          {/*
            Columns, Sort and Save behind one trigger rather than three —
            search is the control worth the row below `md` (#357). Full
            labels inside, not the icon-only `compact` buttons the desktop
            row uses, since there is no neighbouring icon here for a bare
            icon to be read against.
          */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                type="button"
                size="icon-sm"
                variant="outline"
                className="shrink-0"
                aria-label="More list options"
              >
                <Ellipsis />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              align="end"
              className="flex w-auto flex-col items-stretch gap-1 p-2"
            >
              {columnChooser}
              {sortControl}
              {saveButton}
              {moreActions}
            </PopoverContent>
          </Popover>
        </div>

        <div className="flex min-w-0 items-center gap-2">
          <div className="min-w-0 flex-1">
            <ViewTabs
              scope={scope}
              savedViews={savedViews}
              emptyLabel={emptyLabel}
              current={current}
              onSelect={onSelectView}
              countOf={countOf}
            />
          </div>
          {layoutSwitcher}
          {/* The list's "Select" portals in here (`ListToolsSlotContext`). */}
          {toolsSlot ? <span ref={toolsSlot} className="contents" /> : null}
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-2">
      <div className="flex min-w-0 items-center gap-2">
        {viewPicker}
        {filterInput}

        <div className="flex shrink-0 items-center gap-2">
          {columnChooser}
          {sortControl}
          {layoutSwitcher}
          {saveButton}
        </div>
      </div>
      {commonFilters}
    </div>
  );
}
